import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, IndianRupee, User, Phone, Mail, Clock, TrendingUp, CheckCircle, XCircle, AlertCircle, Plus, Download, X, Loader2, PiggyBank, RefreshCw, AlertTriangle } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { formatFullName } from '@/utils/nameFormatter';

function LavajamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  // Sync History States
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncDate, setSyncDate] = useState('');
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    month: '',
    amount_paid: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    receipt_number: '',
    notes: ''
  });

  const [closeForm, setCloseForm] = useState({
    closure_reason: ''
  });

  useEffect(() => {
    fetchAccountDetails();
  }, [id]);

  const fetchAccountDetails = async () => {
    try {
      const data = await api.lavajam.getOne(id);
      setAccount(data);
    } catch (error) {
      console.error('Error fetching account:', error);
      toast.error('Failed to load account details');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setSubmittingPayment(true);

    try {
      // For now, we only support single month payment in this simplified version
      // The backend supports bulk, but let's stick to the basic requirement first
      await api.lavajam.recordPayment(id, {
        month: paymentForm.month,
        amount_paid: parseFloat(paymentForm.amount_paid),
        payment_date: paymentForm.payment_date,
        payment_mode: paymentForm.payment_mode,
        receipt_number: paymentForm.receipt_number,
        notes: paymentForm.notes
      });

      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      fetchAccountDetails();

      // Reset form
      setPaymentForm({
        month: '',
        amount_paid: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_mode: 'cash',
        receipt_number: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleCloseAccount = async (e) => {
    e.preventDefault();
    try {
      await api.lavajam.close(id);
      toast.success('Account closed successfully');
      setCloseDialogOpen(false);
      fetchAccountDetails();
    } catch (error) {
      toast.error('Failed to close account');
    }
  };

  const openSyncDialog = () => {
    setSyncDate(account.start_date);
    setSyncPreview(null);
    setSyncDialogOpen(true);
  };

  const handlePreviewSync = async () => {
    if (!syncDate) return;
    setSyncing(true);
    try {
      const preview = await api.lavajam.previewBackfill({
        start_date: syncDate,
        monthly_amount: account.monthly_amount
      });
      setSyncPreview(preview);
    } catch (error) {
      toast.error('Failed to calculate preview');
    } finally {
      setSyncing(false);
    }
  };

  const handleConfirmSync = async () => {
    setSyncing(true);
    try {
      await api.lavajam.syncHistory(id, { start_date: syncDate });
      toast.success('Account history synced successfully');
      setSyncDialogOpen(false);
      fetchAccountDetails();
    } catch (error) {
      toast.error('Failed to sync history');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };

    return (
      <Badge className={variants[status] || variants.active}>
        {status === 'active' ? '🟢 Active' : '🔴 Closed'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout role="manager">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!account) {
    return (
      <DashboardLayout role="manager">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900">Account Not Found</h2>
          <Button onClick={() => navigate('/manager/lavajam')} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to List
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="manager">
      <div className="space-y-6 fade-in pt-2">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/manager/lavajam')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {account.account_number}
              </h1>
              <div className="flex items-center flex-wrap gap-1 sm:space-x-2 mt-1">
                {getStatusBadge(account.status)}
                <span className="text-gray-500 hidden sm:inline">•</span>
                <span className="text-sm sm:text-base text-gray-600">Lavajam Account</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2 w-full sm:w-auto">
            {account.status === 'active' && (
              <>
                <Button variant="outline" onClick={openSyncDialog} title="Sync History" className="w-full sm:w-auto">
                  <RefreshCw className="h-4 w-4 mr-2" /> Sync History
                </Button>
                <Button variant="destructive" onClick={() => setCloseDialogOpen(true)} className="w-full sm:w-auto">
                  <XCircle className="h-4 w-4 mr-2" /> Close Account
                </Button>
                <Button className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700" onClick={() => setPaymentDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Record Payment
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Member Info Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Account Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Account Holder</p>
                  <div className="flex items-center font-medium gap-2">
                    <div className="w-6 h-6 rounded bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-sm text-white font-bold text-xs">
                      {account.member?.first_name ? account.member.first_name.charAt(0) : ''}
                      {account.member?.last_name ? account.member.last_name.charAt(0) : ''}
                    </div>
                    {formatFullName(account.member?.first_name, account.member?.middle_name, account.member?.last_name)}
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Monthly Contribution</p>
                  <div className="flex items-center font-medium text-emerald-600">
                    <IndianRupee className="h-4 w-4 mr-2" />
                    {formatCurrency(account.monthly_amount)}
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Total Deposited</p>
                  <div className="flex items-center font-medium text-green-600">
                    <PiggyBank className="h-4 w-4 mr-2" />
                    {formatCurrency(account.total_deposited)}
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Start Date</p>
                  <div className="flex items-center font-medium">
                    <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                    {formatDate(account.start_date)}
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Due Date</p>
                  <div className="flex items-center font-medium">
                    <Clock className="h-4 w-4 mr-2 text-gray-400" />
                    20th of every month
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Account Type</p>
                  <div className="flex items-center font-medium">
                    <TrendingUp className="h-4 w-4 mr-2 text-gray-400" />
                    Profit Sharing
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contact Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-gray-400" />
                <span>{account.member?.contact_number}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="truncate" title={account.member?.email || 'No email provided'}>{account.member?.email || 'No email provided'}</span>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-1">Address</p>
                <p className="text-sm">{account.member?.address || 'No address provided'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Payment Date</TableHead>
                  <TableHead>Amount Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Receipt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {account.payments && account.payments.length > 0 ? (
                  account.payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="font-medium">{payment.month}</TableCell>
                      <TableCell>{formatDate(payment.due_date)}</TableCell>
                      <TableCell>{payment.payment_date ? formatDate(payment.payment_date) : '-'}</TableCell>
                      <TableCell>{payment.amount_paid ? formatCurrency(payment.amount_paid) : '-'}</TableCell>
                      <TableCell>
                        <Badge variant={
                          payment.payment_type === 'on_time' ? 'success' :
                            payment.payment_type === 'late' ? 'destructive' :
                              payment.payment_type === 'advance' ? 'default' : 'secondary'
                        }>
                          {payment.payment_type?.replace('_', ' ').toUpperCase() || 'PENDING'}
                        </Badge>
                      </TableCell>
                      <TableCell>{payment.receipt_number || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No payment history available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
              <DialogDescription>
                Record a monthly contribution for {account.account_number}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handlePaymentSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="month">Month (YYYY-MM)</Label>
                <Input
                  id="month"
                  type="month"
                  value={paymentForm.month}
                  onChange={(e) => setPaymentForm({ ...paymentForm, month: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount (₹)</Label>
                <Input
                  id="amount"
                  type="number"
                  value={paymentForm.amount_paid}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount_paid: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Payment Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mode">Payment Mode</Label>
                <Select
                  value={paymentForm.payment_mode}
                  onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_mode: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="receipt">Receipt Number</Label>
                <Input
                  id="receipt"
                  value={paymentForm.receipt_number}
                  onChange={(e) => setPaymentForm({ ...paymentForm, receipt_number: e.target.value })}
                  placeholder="Auto-generated if empty"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submittingPayment} className="bg-emerald-600 hover:bg-emerald-700">
                  {submittingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record Payment'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Close Account Dialog */}
        <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Close Account</DialogTitle>
              <DialogDescription>
                Are you sure you want to close this account? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-yellow-50 p-4 rounded-md text-sm text-yellow-800 flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <p>
                  Closing this account will mark it as inactive. The member will receive their total deposited amount plus any accumulated profit share.
                </p>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleCloseAccount}>
                  Confirm Closure
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Sync History Dialog */}
        <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
          <DialogContent className="w-[95vw] max-w-md sm:w-full">
            <DialogHeader>
              <DialogTitle>Sync Account History</DialogTitle>
              <DialogDescription>
                Update the start date and generate missing payment records.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={syncDate}
                    onChange={(e) => {
                      setSyncDate(e.target.value);
                      setSyncPreview(null);
                    }}
                  />
                  <Button onClick={handlePreviewSync} disabled={syncing || !syncDate}>
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview'}
                  </Button>
                </div>
              </div>

              {syncPreview && (
                <div className="bg-gray-50 p-4 rounded-md space-y-3 border">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Months Passed:</span>
                    <span className="font-medium">{syncPreview.months_passed}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Amount:</span>
                    <span className="font-medium">{formatCurrency(syncPreview.total_amount)}</span>
                  </div>
                  {syncPreview.months_passed > 0 && (
                    <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800 flex items-start gap-2">
                      <AlertTriangle className="h-3 w-3 mt-0.5" />
                      <span>This will generate {syncPreview.months_passed} "Paid" records automatically.</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setSyncDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleConfirmSync}
                  disabled={syncing || !syncPreview}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Confirm Sync
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default LavajamDetails;
